import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChainId } from 'wagmi';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { orpc } from '@/shared/api/client';
import { UsersTab } from './UsersTab';
import { WebhooksTab } from './WebhooksTab';

type ConfigResponse = {
  claimrApiUrl: string;
  claimrCampaignIds: Record<string, string>;
};

export function ClaimrPage() {
  const chainId = useChainId();
  const [syncing, setSyncing] = useState(false);

  const { data: config } = useQuery({
    queryKey: ['config'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      return res.json() as Promise<ConfigResponse>;
    },
    staleTime: Infinity,
  });

  const currentCampaignId = config?.claimrCampaignIds?.[chainId];

  const handleSyncVeGNET = async () => {
    setSyncing(true);
    try {
      const result = await orpc.sync.triggerVeGNET({});
      toast.success(`veGNET sync complete: ${result.processed} users processed`);
    } catch (error) {
      toast.error('Sync failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2b2b2b]">Claimr</h1>
          <p className="text-[#807f7e]">Users and webhook monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncVeGNET}
            disabled={syncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync veGNET'}
          </Button>
          {currentCampaignId && (
            <div className="flex items-center gap-2 text-sm text-[#807f7e]">
              <span>Campaign:</span>
              <Badge variant="outline" className="font-mono">
                {currentCampaignId}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersTab />
        </TabsContent>

        <TabsContent value="webhooks" className="mt-4">
          <WebhooksTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
